import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Link, useLocation, createSearchParams } from 'react-router'

import colors from 'components/utils/colors'
import IconLink from 'components/Shared/IconLink/Index'
import { stringifyQuery, parsedQuery } from 'utils/strings'

const StyledWrapper = styled.section`
  display: flex;
  align-items: center;
  height: 16px;
  padding-top: 16px;
  justify-content: flex-end;

  ${props => props.disabled && css`
    pointer-events: none;
  `}
`

const StyledPageLink = styled(Link)`
  color: ${colors.secondary40};
  padding: 0 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;

  ${props => props.$active && css`
    color: ${colors.primary40};
  `}
`

const StyledArrowIconLink = styled(IconLink)`
  padding: 0 8px;
`

const Pagination = ({ activePage = 1, totalPages = 1, disabled, className, pageParamName = 'page' }) => {
  const location = useLocation()

  if (totalPages <= 1) {
    return null
  }

  if (activePage > totalPages) {
    activePage = totalPages
  }

  const PaginationParams = (page) => {
    const params = parsedQuery(location.search)
    params[pageParamName] = page

    return `?${createSearchParams(stringifyQuery(params))}`
  }

  // eslint-disable-next-line react/prop-types
  const PageLink = ({ page, text }) => {
    const active = page === activePage

    // $active is transient prop to remove React warning: https://github.com/styled-components/styled-components/pull/3052
    return (
      <StyledPageLink
        data-test='pagination-link'
        $active={active}
        to={{
          pathname: location.pathname,
          search: PaginationParams(page),
        }}
      >
        {text || page}
      </StyledPageLink>
    )
  }

  // eslint-disable-next-line react/prop-types
  const ArrowLink = ({ page, iconName }) => {
    return (
      <Link
        to={{
          pathname: location.pathname,
          search: PaginationParams(page),
        }}
      >
        <StyledArrowIconLink iconName={iconName} />
      </Link>
    )
  }

  const prevPage = activePage - 1
  const nextPage = activePage + 1
  const middlePage1 = Math.round((1 + prevPage) / 2)
  const middlePage2 = Math.round((nextPage + totalPages) / 2)
  const showActivePage = Boolean(activePage > 1 && activePage < totalPages)

  return (
    <StyledWrapper disabled={disabled} className={className}>
      {activePage > 1 ? <ArrowLink iconName='icon-arrow-left' page={prevPage} /> : null}
      <PageLink page={1} />

      {prevPage <= 2 ? null : <PageLink text='...' page={middlePage1} />}
      {prevPage <= 1 ? null : <PageLink page={prevPage} />}

      {showActivePage ? <PageLink page={activePage} /> : null}

      {nextPage >= totalPages ? null : <PageLink page={nextPage} />}
      {nextPage >= totalPages - 1 ? null : <PageLink text='...' page={middlePage2} />}

      <PageLink page={totalPages} />
      {activePage < totalPages ? <ArrowLink iconName='icon-arrow-right1' page={nextPage} /> : null}
    </StyledWrapper>
  )
}

Pagination.propTypes = {
  activePage: PropTypes.number,
  totalPages: PropTypes.number,
  disabled: PropTypes.bool,
  pageParamName: PropTypes.string,
}

export default Pagination
